import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';
import { CurrentUserService } from './current-user.service';
import { JsonHttpService } from '../../shared/http/json-http.service';
import { User } from '../models/user';

@Injectable({
    providedIn: 'root'
})
export class UserService {

    private readonly currentUserService = inject(CurrentUserService);
    private readonly httpService = inject(JsonHttpService);
    private readonly usersForPresentationUrl = '/api/users/get-users-for-presentation';
    private simpleUsers:  User[] = [];
    private isLoading = false;

    /**
     * Gets the requested users or all presentation users when no IDs are supplied.
     * @param userIds Optional user IDs to retrieve individually.
     * @returns The requested users.
     */
    async getUsers(userIds?: number[]): Promise<User[]> {
        const users = userIds
            ? await Promise.all(userIds.map(userId => this.getUser(userId)))
            : await this.getUsersForPresentation();
        return users;
    }

    /**
     * Gets all users required by presentation and selection views.
        * @returns The requested users.
     */
    getUsersForPresentation(): Promise<User[]> {
        return firstValueFrom(this.httpService.getTyped<User[]>(this.usersForPresentationUrl).pipe(
            map(users => users.map(user => this.normalizeUser(user)))
        ));
    }

    /**
     * Gets one user by ID.
     * @param userId ID of the user to retrieve.
        * @returns The requested user.
     */
    getUser(userId: number): Promise<User> {
        const options = { params: new HttpParams().set('userId', `${userId}`) };
        return firstValueFrom(this.httpService.getTyped<User>('/api/users/get-user', options).pipe(
            map(user => this.normalizeUser(user))
        ));
    }

    /**
    * Gets the authenticated user from the cache or API and refreshes the cache when loaded remotely.
    * @param alwaysReload Whether to bypass the cached user and request the API.
    * @param useGenericErrorHandling Whether the HTTP service should apply generic error handling.
    * @returns The current user.
     */
    async getCurrentUser(alwaysReload: boolean = true, useGenericErrorHandling = true): Promise<User> {
        let user = this.currentUserService.getCurrentUser();
        if (!user || alwaysReload) {
            user = await firstValueFrom(this.httpService.getTyped<User>('/api/users/get-current-user', undefined, useGenericErrorHandling).pipe(
                map(response => this.normalizeUser(response))
            ));
            this.currentUserService.setCurrentUser(user);
        }

        return user;
    }

    /**
     * Creates or updates a user.
     * @param user User data to persist.
     * @returns The persisted user.
     */
    saveUser(user: User): Promise<User> {
        return firstValueFrom(this.httpService.postTyped<User>('/api/users/save-user/', user).pipe(
            map(response => this.normalizeUser(response))
        ));
    }

    /**
     * Deletes a user.
     * @param userId ID of the user to delete.
     * @returns A promise that resolves when deletion completes.
     */
    deleteUser(userId: number): Promise<void> {
        const options = { params: new HttpParams().set('userId', `${userId}`) };
        return firstValueFrom(this.httpService.post('/api/users/delete-user/', null, options));
    }

    /**
     * Sets a user's password.
     * @param user User containing the new password.
     * @returns A promise that resolves when the password is set.
     */
    setUserPassword(user: User): Promise<void> {
        return firstValueFrom(this.httpService.post('/api/users/set-user-password/', user));
    }

    /**
     * Checks whether a username is available, optionally excluding the specified user.
     * @param userId ID of the existing user whose username may remain unchanged.
     * @param username Username to validate.
     * @returns Whether the username is available.
     */
    isUsernameAvailable(userId: number, username: string): Promise<boolean> {
        let params = new HttpParams().set('username', username);
        if (userId) {
            params = params.set('userId', `${userId}`);
        }
        return firstValueFrom(this.httpService.getTyped<boolean>('/api/users/is-username-available', { params }));
    }

    /**
     * Changes a user's username.
     * @param userId ID of the user to update.
     * @param newUsername Replacement username.
     * @returns A promise that resolves when the username is changed.
     */
    changeUsername(userId: number, newUsername: string): Promise<void> {
        const options = {
            params: new HttpParams()
                .set('userId', `${userId}`)
                .set('username', newUsername)
        };
        return firstValueFrom(this.httpService.postTyped<void>('/api/users/set-username', undefined, options));
    }

    /**
     * Generates and sends a new multi-factor authentication secret.
     * @param userId ID of the user for whom to generate a secret.
     * @returns A promise that resolves when the secret is generated.
     */
    generateNewSecret(userId: number): Promise<void> {
        const options = { params: new HttpParams().set('userId', `${userId}`) };
        return firstValueFrom(this.httpService.postTyped<void>('/api/users/generate-new-secret', undefined, options));
    }

    /**
     * Resends a user's multi-factor authentication secret.
     * @param userId ID of the user whose secret should be resent.
     * @returns A promise that resolves when the secret is sent.
     */
    resendSecret(userId: number): Promise<void> {
        const options = { params: new HttpParams().set('userId', `${userId}`) };
        return firstValueFrom(this.httpService.postTyped<void>('/api/users/resend-secret', undefined, options));
    }

    /**
     * Disables multi-factor authentication by clearing a user's secret.
     * @param userId ID of the user whose secret should be cleared.
     * @returns A promise that resolves when the secret is cleared.
     */
    clearSecret(userId: number): Promise<void> {
        const options = { params: new HttpParams().set('userId', `${userId}`) };
        return firstValueFrom(this.httpService.postTyped<void>('/api/users/clear-secret', undefined, options));
    }

    /**
     * Checks whether multi-factor authentication is enabled for a user.
     * @param userId ID of the user to check.
     * @returns Whether multi-factor authentication is enabled.
     */
    isMfaEnabled(userId: number): Promise<boolean> {
        const options = { params: new HttpParams().set('userId', `${userId}`) };
        return firstValueFrom(this.httpService.postTyped<boolean>('/api/users/is-mfa-enabled', undefined, options));
    }

    /**
     * Refreshes the authenticated user's server-side permissions.
     * @returns A promise that resolves when the permissions are refreshed.
     */
    refreshPermissionsForCurrentUser(): Promise<void> {
        return firstValueFrom(this.httpService.post('/api/users/refresh-permissions-current-user', undefined));
    }

    /**
     * Revokes a user's tokens for a client application.
     * @param userId ID of the user whose tokens should be revoked.
     * @param clientId ID of the client application.
     * @returns A promise that resolves when the tokens are revoked.
     */
    revokeTokens(userId: number, clientId: string): Promise<void> {
        const options = {
            params: new HttpParams()
                .set('userId', `${userId}`)
                .set('clientId', `${clientId}`)
        };
        return firstValueFrom(this.httpService.post('/api/users/revoke-tokens', null, options));
    }

    /**
     * Looks up a user in the simple-user cache and starts a cache load when it is missing.
     * @param userId ID of the user to find.
     * @returns The cached user, or null until the cache has loaded or when no ID is supplied.
     */
    getSimpleUser(userId: number): User | null {
        const user = userId ? this.simpleUsers.find(candidate => candidate.UserId === userId) ?? null : null;
        if (userId && !user) {
            void this.loadSimpleUsers();
        }
        return user;
    }

    /**
     * Loads simplified user records into the local cache unless a load is already in progress.
     * @returns A promise that resolves after the cache is populated.
     */
    async loadSimpleUsers(): Promise<void> {
        if (!this.isLoading) {
            this.isLoading = true;
            try {
                const options = {
                    params: new HttpParams()
                        .set('includeDeleted', true)
                        .set('includeDisabled', true)
                };
                this.simpleUsers = await firstValueFrom(this.httpService.getTyped<User[]>('/api/users/get-simple-users', options, true).pipe(
                    map(users => users.map(user => this.normalizeUser(user)))
                ));
            }
            finally {
                this.isLoading = false;
            }
        }
    }

    /**
     * Converts a transferred activity value to a Date while retaining all remaining user data.
     * @param user User returned from the API.
        * @returns A copy of the user.
     */
    private normalizeUser(user: User): User {
        return { ...user, LastActivity: user.LastActivity ? new Date(user.LastActivity) : null };
    }
}
